import {
    getPatientImageSeries,
    patchImageLabel,
    postDicomInstances,
} from '~/backend/connector-next-api/imageSaveClient';
import { PersistenceRequestError } from '~/domain/persistence/PersistenceErrors';
import type {
    ImagePersistenceGateway,
    PersistedImageSeries,
    UploadedImageBatch,
} from '~/domain/persistence/ImagePersistenceGateway';
import type {
    ConnectorDatasetId,
    ConnectorPatientId,
    ConnectorVersionToken,
} from '~/domain/persistence/PersistenceRef';
import {
    asConnectorDatasetId,
    asConnectorVersionToken,
} from '~/domain/persistence/PersistenceRef';

/** connector-next implementation of the image persistence port. */
export class ConnectorImageGateway implements ImagePersistenceGateway {
    async uploadInstances(files: readonly Blob[]): Promise<UploadedImageBatch> {
        const { rows, afterInterruptedAttempt } =
            await postDicomInstances(files);
        return {
            rows: rows.map((row) => ({
                datasetId: asConnectorDatasetId(row.id),
                seriesInstanceUid: row.seriesInstanceUid,
                version: asConnectorVersionToken(row.version),
                seriesDescription: row.seriesDescription,
                wasNew: row.wasNew,
            })),
            afterInterruptedAttempt,
        };
    }

    async listPatientImageSeries(
        patientId: ConnectorPatientId
    ): Promise<readonly PersistedImageSeries[]> {
        const rows = await getPatientImageSeries(patientId);
        return rows.map((row) => ({
            datasetId: asConnectorDatasetId(row.id),
            seriesInstanceUid: row.seriesInstanceUid,
            version: asConnectorVersionToken(row.version),
            seriesDescription: row.seriesDescription,
        }));
    }

    async updateLabel(
        id: ConnectorDatasetId,
        ifMatch: ConnectorVersionToken,
        seriesDescription: string
    ): Promise<{ readonly version: ConnectorVersionToken }> {
        const response = await patchImageLabel(id, ifMatch, seriesDescription);
        if (!response.version) {
            // Without the post-update token the next If-Match would be
            // stale bookkeeping — treat as a failed request.
            throw new PersistenceRequestError(null, null);
        }
        return { version: asConnectorVersionToken(response.version) };
    }
}
